<template>
  <div class="personnel-utilization-summary">
    <div class="subtitle-2 font-weight-medium mb-2">Personalauslastung:</div>

    <!-- Progress Bar showing personnel assignment -->
    <div class="mb-3">
      <div class="position-relative">
        <!-- Main progress bar showing assigned vs required -->
        <v-progress-linear
          :value="progressValue"
          height="32"
          rounded
          :color="progressColor"
          :background-color="progressBackgroundColor"
          class="mb-2"
        >
          <template v-slot:default="{ value }">
            <div class="d-flex align-center justify-center fill-height px-2">
              <span
                class="font-weight-medium"
                style="z-index: 100"
                :class="value > 50 ? 'white--text' : 'black--text'"
              >
                {{ fulfillmentStatus.totalAvailable }}
                /
                {{ fulfillmentStatus.totalRequired }}
                Personen
                <span
                  v-if="fulfillmentStatus.remainingPersonnel > 0"
                  class="ml-2 caption"
                  :class="value > 50 ? 'white--text' : 'blue--text'"
                >
                  (+{{ fulfillmentStatus.remainingPersonnel }} verfügbar)
                </span>
              </span>
            </div>
          </template>
        </v-progress-linear>

        <!-- Target line at 100% requirement fulfillment -->
        <div
          class="target-line"
          :style="{
            left: progressValue + '%',
            display:
              fulfillmentStatus.remainingPersonnel > 0 ? 'block' : 'none',
          }"
        >
          <span class="target-label">Bedarf</span>
        </div>
      </div>

      <!-- Status chips -->
      <div class="d-flex align-center justify-space-between flex-wrap">
        <v-chip
          small
          :color="progressColor"
          text-color="white"
          class="mr-2 mb-1"
        >
          <v-icon left x-small>mdi-account-check</v-icon>
          {{ fulfillmentStatus.totalAvailable }} zugewiesen
        </v-chip>

        <v-chip
          v-if="fulfillmentStatus.remainingPersonnel > 0"
          small
          color="blue"
          text-color="white"
          class="mr-2 mb-1"
        >
          <v-icon left x-small>mdi-account-multiple</v-icon>
          {{ fulfillmentStatus.remainingPersonnel }} verfügbar
        </v-chip>

        <!-- Surplus personnel chip -->
        <v-chip
          v-if="totalSurplus > 0"
          small
          color="blue"
          text-color="white"
          class="mr-2 mb-1"
        >
          <v-icon left x-small>mdi-account-plus</v-icon>
          {{ totalSurplus }} Überschuss
        </v-chip>

        <!-- Missing personnel chip -->
        <v-chip
          v-if="totalMissing > 0"
          small
          color="red"
          text-color="white"
          class="mr-2 mb-1"
        >
          <v-icon left x-small>mdi-account-minus</v-icon>
          {{ totalMissing }} fehlen
        </v-chip>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PersonnelUtilizationSummary",
  props: {
    fulfillmentStatus: {
      type: Object,
      required: true,
    },
  },
  computed: {
    totalSurplus() {
      return Math.max(
        0,
        this.fulfillmentStatus.totalAvailable -
          this.fulfillmentStatus.totalRequired
      );
    },
    totalMissing() {
      return Math.max(
        0,
        this.fulfillmentStatus.totalRequired -
          this.fulfillmentStatus.totalAvailable
      );
    },
    progressValue() {
      const totalAvailable = this.fulfillmentStatus.totalAvailable;
      const totalRequired = Math.max(this.fulfillmentStatus.totalRequired, 1);
      const remainingPersonnel = this.fulfillmentStatus.remainingPersonnel || 0;

      if (remainingPersonnel > 0) {
        const totalPersonnel = totalAvailable + remainingPersonnel;
        return (totalRequired / Math.max(totalPersonnel, 1)) * 100;
      } else {
        return (totalAvailable / totalRequired) * 100;
      }
    },
    progressColor() {
      const percentage =
        (this.fulfillmentStatus.totalAvailable /
          Math.max(this.fulfillmentStatus.totalRequired, 1)) *
        100;
      const cappedPercentage = Math.min(percentage, 100);

      if (cappedPercentage >= 100) return "success";
      if (cappedPercentage >= 75) return "light-green";
      if (cappedPercentage >= 50) return "orange";
      if (cappedPercentage >= 25) return "deep-orange";
      return "red";
    },
    progressBackgroundColor() {
      return this.fulfillmentStatus.remainingPersonnel > 0
        ? "blue lighten-4"
        : "grey lighten-4";
    },
  },
};
</script>

<style scoped>
.position-relative {
  position: relative;
}

.target-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #333;
  opacity: 0.8;
  transform: translateX(-1px);
}

.target-line::before {
  content: "";
  position: absolute;
  top: -4px;
  left: -3px;
  width: 8px;
  height: 4px;
  background-color: #333;
  border-radius: 2px;
}

.target-line::after {
  content: "";
  position: absolute;
  bottom: -4px;
  left: -3px;
  width: 8px;
  height: 4px;
  background-color: #333;
  border-radius: 2px;
}

.target-label {
  position: absolute;
  top: -26px;
  left: -12px;
  font-size: 10px;
  font-weight: bold;
  background-color: white;
  padding: 0px 3px;
  border-radius: 2px;
  border: 1px solid #333;
}
</style>
